/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:34:32AM]
Permissions to update outside the main branch: [false]
*/

import {
    Filter,
    getFilterElementsDefault,
    useWorkplaceState,
    WorkplaceBtnActions,
    WorkplaceBtnDelete,
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    addDeclinedShowcase,
    changeDeclinedShowcase,
    deleteDeclinedShowcase,
    getDeclinedShowcase,
    getDeclinedShowcasesExcel,
    getDeclinedShowcasesList,
} from 'axios/declinedShowcasesApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    DeclinedShowcase,
    DeclinedShowcaseAdd,
    DeclinedShowcaseChange,
    DeclinedShowcaseTable,
} from 'models/api/declinedShowcase';
import { useCallback, useState } from 'react';
import { FormatterProps } from 'react-data-grid';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import './DeclinedShowcase.scss';
import DeclinedShowcaseAddButton from './DeclinedShowcaseAddButton';
import DeclinedShowcasesChangeModal from './DeclinedShowcaseChangeModal';
import DeclinedShowcaseFilter from './DeclinedShowcasesFilter';
import LeftColumn from './LeftColumn';

type DeclinedShowcasesFormatterProps = FormatterProps<
    DeclinedShowcaseTable,
    DeclinedShowcaseTable[]
>;

const DeclinedShowcases = () => {
    const { t } = useTranslation();
    const api = useApi();
    const { hasRole } = useAuth();
    const queryClient = useQueryClient();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const rootClassName = 'declined-showcase';
    const className = `${rootClassName}-settings`;

    const logoVirtualPathFormatter = useCallback(
        ({ row: { logoVirtualPath } }: DeclinedShowcasesFormatterProps) => (
            <img
                src={logoVirtualPath}
                alt={t('BackOfficeLogoNotFound')}
                className={`${rootClassName}__img`}
            />
        ),
        []
    );

    const actionsFormatter = useCallback(
        ({ row: { id } }: DeclinedShowcasesFormatterProps) => (
            <WorkplaceBtnActions>
                {hasRole('declinedShowcasesDelete') && (
                    <WorkplaceBtnDelete key={`delete-btn-${id}`} id={id} />
                )}
            </WorkplaceBtnActions>
        ),
        []
    );

    return (
        <Col>
            <Row className={rootClassName}>
                <Col xs="4" className="main-card">
                    <Card className={className}>
                        <CardHeader>
                            <div className={`${className}__header`}>
                                <h4>
                                    <Trans i18nKey="BackOfficeSettings" />
                                </h4>
                            </div>
                        </CardHeader>
                        <CardBody className={`${className}__body`}>
                            <LeftColumn />
                        </CardBody>
                    </Card>
                </Col>
                <Col xs="8">
                    <BOWorkplace<
                        DeclinedShowcaseTable,
                        DeclinedShowcase,
                        Filter<DeclinedShowcase>,
                        DeclinedShowcaseChange,
                        DeclinedShowcaseAdd
                    >
                        testId="declined-showcases"
                        pageTitle="BackOfficeShowcaseSettingsForRefuseniks"
                        filterParams={{
                            id: '',
                        }}
                        initColumns={[
                            {
                                key: 'id',
                                name: <Trans i18nKey="BackOfficeId" />,
                                width: 70,
                                minWidth: 70,
                            },
                            {
                                key: 'logoVirtualPath',
                                name: <Trans i18nKey="BackOfficeLogo" />,
                                formatter: logoVirtualPathFormatter,
                            },
                            {
                                key: 'referralLink',
                                name: <Trans i18nKey="BackOfficeReferralLink" />,
                            },
                            { key: 'lendingData', name: <Trans i18nKey="BackOfficeLendingData" /> },
                            { key: 'dsc', name: <Trans i18nKey="BackOfficeDescription" /> },
                            {
                                key: 'actions',
                                name: <FontAwesomeIcon icon={faBoltLightning} />,
                                cellClass: 'cell-centered no-change-modal',
                                headerCellClass: 'cell-centered',
                                width: 70,
                                minWidth: 70,
                                draggable: false,
                                resizable: false,
                                clickDisable: true,
                                formatter: actionsFormatter,
                            },
                        ]}
                        storagePath="declinedShowcases/columns"
                        getFilterElements={getFilterElementsDefault([
                            { id: 'id', name: t('BackOfficeId') },
                        ])}
                        showModal={
                            hasRole('declinedShowcasesGetById')
                                ? () => setIsModalVisible(true)
                                : undefined
                        }
                        buttons={
                            hasRole('declinedShowcasesCreate')
                                ? [
                                      <DeclinedShowcaseAddButton
                                          key="1"
                                          showModal={() => setIsModalVisible(true)}
                                      />,
                                  ]
                                : []
                        }
                        filter={<DeclinedShowcaseFilter />}
                        modal={{
                            component: isModalVisible && (
                                <DeclinedShowcasesChangeModal
                                    isOpen={isModalVisible}
                                    toggle={setIsModalVisible}
                                />
                            ),
                            fetchCurrentItemQueryFn: (id) => getDeclinedShowcase(api, id),
                            changeMutationFn: (item) => changeDeclinedShowcase(api, item),
                            addMutationFn: (item) => addDeclinedShowcase(api, item),
                        }}
                        deleteMutationFn={(id) => deleteDeclinedShowcase(api, id)}
                        fetchQueryFn={(filter) => getDeclinedShowcasesList(api, filter)}
                        queryKey={['declinedShowcases']}
                        queryClient={queryClient}
                        fetchExcelQueryFn={(filter) => getDeclinedShowcasesExcel(api, filter)}
                        lineHeight={70}
                        showExcelBtn={hasRole('declinedShowcasesExcel')}
                    />
                </Col>
            </Row>
        </Col>
    );
};

const useDeclinedShowcasesWorkplaceState = () =>
    useWorkplaceState<
        DeclinedShowcaseTable,
        DeclinedShowcase,
        DeclinedShowcaseChange,
        DeclinedShowcaseAdd
    >();

export default DeclinedShowcases;
export { useDeclinedShowcasesWorkplaceState };
